import CloseIcon from "@mui/icons-material/Close";
import { Container, IconButton, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  IoAdd,
  IoAddOutline,
  IoRefresh,
  IoRefreshOutline,
  IoSave,
  IoSaveOutline,
  IoSettings,
  IoSettingsOutline,
} from "react-icons/io5";

import { gql, useMutation } from "@apollo/client";
import { Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { HeaderGrid } from "components/atoms/HeaderGrid";
import { LargeSpinner } from "components/atoms/LargeSpinner";
import { useDDFilter } from "components/modules/disinfectionFilter/DisinfectionFilterPanel";
import { AverageDurationChart } from "components/organisms/charts/AverageDurationChart";
import { CompletionChart } from "components/organisms/charts/CompletionChart";
import { CycleOverviewChart } from "components/organisms/charts/CycleOverviewChart";
import { CyclesCounterChart } from "components/organisms/charts/CyclesCounterChart";
import { DisinfectionModesChart } from "components/organisms/charts/DisinfectionModesChart";
import { DisinfectionsReportsTable } from "components/organisms/charts/DisinfectionReportsTable";
import { DisinfectionsByDepartmentTable } from "components/organisms/charts/DisinfectionsByDepartmentTable";
import { DisinfectionsByOperatorTable } from "components/organisms/charts/DisinfectionsByOperatorTable";
import { DisinfectionsByRoomTable } from "components/organisms/charts/DisinfectionsByRoomTable";
import { DisinfectionsInterruptedChart } from "components/organisms/charts/DisinfectionsInterruptedChart";
import { DisinfectionsPerRoomAcrossTimeGraph } from "components/organisms/charts/DisinfectionsPerRoomAcrossTimeGraph";
import { InterruptionsOverviewChart } from "components/organisms/charts/InterruptionsOverviewChart";
import { PositionsReachedChart } from "components/organisms/charts/PositionsReachedChart";
import { UniqueDepartmentsChart } from "components/organisms/charts/UniqueDepartmentsChart";
import { UniqueOperatorsChart } from "components/organisms/charts/UniqueOperatorsChart";
import { UniqueRoomsChart } from "components/organisms/charts/UniqueRoomsChart";
import { DisinfectionDashboardFilter } from "components/organisms/DisinfectionDashboardFilter";
import { GroupBy } from "gql/graphql";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { Layout, Layouts, Responsive } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useParams } from "react-router-dom";
import { getIsForbidden } from "utils/privileges/privilegeUtils";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { Organization } from "../../gql/graphql";
import { ForbiddenPage } from "./ForbiddenPage";

const defaultLayouts: Layouts = Object.freeze({
  xl: (() => {
    let y = 0;
    const heights = {
      row1: 1,
      row2: 2,
      row3: 3,
      row4: 5.7,
      row5: 4,
      row6: 3,
      row7: 3,
      row8: 4,
      row9: 4,
      row10: 2,
    };
    return [
      // Row 1
      { i: "CyclesCounterChart", x: 0, y, w: 1, h: heights.row1 },
      { i: "UniqueRoomsChart", x: 1, y, w: 1, h: heights.row1 },
      { i: "UniqueOperatorsChart", x: 2, y, w: 1, h: heights.row1 },

      // Row 2
      {
        i: "CompletionChart",
        x: 0,
        y: (y += heights.row1),
        w: 1,
        h: heights.row2,
      },
      { i: "AverageDurationChart", x: 1, y, w: 1, h: heights.row2 },
      { i: "DisinfectionsInterruptedChart", x: 2, y, w: 1, h: heights.row2 },

      // Row 3
      {
        i: "CycleOverviewChartDaily",
        x: 0,
        y: (y += heights.row2),
        w: 3,
        h: heights.row3,
      },

      // Row 4
      {
        i: "DisinfectionsReportsTable",
        x: 0,
        y: (y += heights.row3),
        w: 3,
        h: heights.row4,
      },

      // Row 5
      {
        i: "DisinfectionsByOperatorTable",
        x: 0,
        y: (y += heights.row4),
        w: 1.5,
        h: heights.row5,
      },
      {
        i: "DisinfectionsPerRoomAcrossTime",
        x: 1.5,
        y: y,
        w: 1.5,
        h: heights.row5,
      },

      // Row 6
      {
        i: "InterruptionsCountByDay",
        x: 0,
        y: (y += heights.row6),
        w: 3,
        h: heights.row6,
      },

      // Row 7
      {
        i: "CycleOverviewChartWeekly",
        x: 0,
        y: (y += heights.row6),
        w: 1.5,
        h: heights.row7,
      },
      { i: "CycleOverviewChartMonthly", x: 1.5, y, w: 1.5, h: heights.row7 },

      // Row 8
      {
        i: "DisinfectionsByRoomTable",
        x: 0,
        y: (y += heights.row7),
        w: 3,
        h: heights.row8,
      },

      // Row 9
      {
        i: "DisinfectionsByDepartmentTable",
        x: 0,
        y: (y += heights.row8),
        w: 3,
        h: heights.row9,
      },

      // Row 10
      {
        i: "DisinfectionModesChart",
        x: 0,
        y: (y += heights.row9),
        w: 1,
        h: heights.row10,
      },
      { i: "PositionsReachedChart", x: 1, y: y, w: 1, h: heights.row10 },
      { i: "UniqueDepartmentsChart", x: 2, y: y, w: 1, h: 1 },
    ];
  })(),
});

const defaultsWidgets = {
  "Disinfection Cycles Count": {
    enabled: true,
  },
  "Unique Rooms Count": {
    enabled: true,
  },
  "Unique Operators Count": {
    enabled: true,
  },
  "Disinfection Count By Mode": {
    enabled: true,
  },
  "Average Duration": {
    enabled: true,
  },
  "Positions Disinfected": {
    enabled: true,
  },
  "Disinfection Reports": {
    enabled: true,
  },
  "Disinfection Count By Day": {
    enabled: true,
  },
  "Disinfection Count By Week": {
    enabled: true,
  },
  "Disinfection Count By Month": {
    enabled: true,
  },
  "Disinfection Count By Operator": {
    enabled: true,
  },
  "Disinfections By Room": {
    enabled: true,
  },
  "Disinfections By Department": {
    enabled: true,
  },
  "Disinfections Completed": {
    enabled: true,
  },
  "Unique Departments Count": {
    enabled: true,
  },
  "Disinfections Interrupted": {
    enabled: true,
  },
  "Interruptions Count By Day": {
    enabled: true,
  },
  "Disinfections Per Room Across Time": {
    enabled: true,
  },
};
const widgetNameToLayoutId: Record<string, string> = {
  CyclesCounterChart: "Disinfection Cycles Count",
  UniqueRoomsChart: "Unique Rooms Count",
  UniqueOperatorsChart: "Unique Operators Count",
  DisinfectionModesChart: "Disinfection Count By Mode",
  AverageDurationChart: "Average Duration",
  PositionsReachedChart: "Positions Disinfected",
  DisinfectionsReportsTable: "Disinfection Reports",
  CycleOverviewChartDaily: "Disinfection Count By Day",
  CycleOverviewChartWeekly: "Disinfection Count By Week",
  CycleOverviewChartMonthly: "Disinfection Count By Month",
  DisinfectionsByOperatorTable: "Disinfection Count By Operator",
  DisinfectionsByRoomTable: "Disinfections By Room",
  DisinfectionsByDepartmentTable: "Disinfections By Department",
  CompletionChart: "Disinfections Completed",
  UniqueDepartmentsChart: "Unique Departments Count",
  DisinfectionsInterruptedChart: "Disinfections Interrupted",
  InterruptionsCountByDay: "Interruptions Count By Day",
  DisinfectionsPerRoomAcrossTime: "Disinfections Per Room Across Time",
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  ArcElement,
  Legend,
);

const CustomWidthProvider = (
  ComposedComponent: any,
  startingWidth?: number,
) => {
  return (props: any) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(startingWidth || 0);

    useEffect(() => {
      if (!elementRef.current) return;

      const resizeObserver = new ResizeObserver(() => {
        const node = elementRef.current;
        if (node) {
          const newWidth = node.getBoundingClientRect().width;
          setWidth(newWidth);
        }
      });

      resizeObserver.observe(elementRef.current);
      setWidth(elementRef.current.getBoundingClientRect().width);

      return () => resizeObserver.disconnect();
    }, []);

    return (
      <div ref={elementRef} className={props.className} style={props.style}>
        <ComposedComponent {...props} width={width} />
      </div>
    );
  };
};

const gridLayoutStyles = (theme: any) => ({
  "&.react-grid-layout": {
    transition: "none",
  },
  "& .react-grid-item": {
    transition: "none",
  },
  "& .react-grid-item.react-grid-placeholder": {
    background: theme.palette.primary.main,
    opacity: 0.4,
    transitionDuration: "100ms",
    zIndex: 2,
    userSelect: "none",
    borderRadius: "10px",
  },
  "& .wiggle-wrapper": {
    width: "100%",
    height: "100%",
  },
  "& .wiggle-wrapper.active": {
    animation: "wiggle 0.5s ease-in-out infinite alternate",
  },
  "@keyframes wiggle": {
    "0%": { transform: "rotate(0deg)" },
    "25%": { transform: "rotate(-0.3deg)" },
    "75%": { transform: "rotate(0.3deg)" },
    "100%": { transform: "rotate(0deg)" },
  },
  "& .drag-handle": {
    height: "100%",
    width: "100%",
  },
});

const HeaderIconButton = styled(IconButton)(({ theme }) => ({
  "&": {
    color: theme.palette.primary.main,
    height: "100%",
    aspectRatio: "1",
    marginLeft: "-8px",
    marginRight: "-8px",
  },
  "& .outline-icon, & .filled-icon": {
    transition: "opacity 500ms",
    position: "absolute",
  },
  "& .outline-icon": {
    opacity: 1,
    visibility: "visible",
  },
  "& .filled-icon": {
    opacity: 0,
    visibility: "hidden",
  },
  "&:hover": {
    background: "transparent",
    "& .outline-icon": {
      opacity: 0,
      visibility: "hidden",
    },
    "& .filled-icon": {
      opacity: 1,
      visibility: "visible",
    },
  },
  "&.rotating": {
    animation: "rotate 2s linear infinite",
  },
  "@keyframes rotate": {
    from: {
      transform: "rotate(0deg)",
    },
    to: {
      transform: "rotate(360deg)",
    },
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  fontStyle: "italic",
  display: "flex",
  alignItems: "center",
  whiteSpace: "nowrap",
}));

const RemoveButton = styled(IconButton)(({}) => ({
  position: "absolute",
  top: "10px",
  right: "10px",
  zIndex: 1,
  backgroundColor: "#ff4444",
  width: "24px",
  height: "24px",
  "&:hover": {
    backgroundColor: "#ff6666",
  },
  "& .MuiSvgIcon-root": {
    color: "white",
    fontSize: "22px",
  },
}));

const QUERY = gql(`
  query DisinfectionDashboardPage($orgIds: [String!]!) {
    organizations(filter: { ids: $orgIds }) {
      id
      name
    }
  }
`);

const MY_CONFIGURATIONS_QUERY = gql(`
  query MyConfigurations {
    me {
      userConfigurations
    }
  }
`);

const UPDATE_MY_CONFIGURATIONS = gql(`
  mutation UpdateMe($payload: UpdateIdentityPayload!) {
    updateMe(payload: $payload) {
      id
    }
  }
`);

type Props = {
  isOnSupportPath: boolean; // Some widgets are not available to supporters. This is temporary until DB schema can be made more easily searchable
};

export const DisinfectionDashboardPage = (props: Props) => {
  const theme = useTheme();
  const { orgId, orgIds } = useParams();
  const orgIdsArray = (orgId ? [orgId] : []).concat(orgIds?.split("+") ?? []);
  const orgIdsFilter =
    orgIdsArray.length > 0 ? { organizationIds: orgIdsArray } : undefined;

  const [filterParams] = useDDFilter();

  ChartJS.defaults.font.family = theme.typography.fontFamily;

  const { data, error, loading } = useQueryWithSnack(QUERY, {
    variables: { orgIds: orgId ? [orgId] : orgIds?.split("+") ?? [] },
    skip: orgId === "support" || !(orgId || orgIds),
  });

  const { data: myConfigurationsData, loading: configurationsLoading } =
    useQueryWithSnack(MY_CONFIGURATIONS_QUERY);

  const [updateMyConfigurations] = useMutation(UPDATE_MY_CONFIGURATIONS, {
    refetchQueries: [
      {
        query: MY_CONFIGURATIONS_QUERY,
      },
    ],
  });

  // Use all possible values saved by user previously.
  // Will fallback to default values required for this version automatically (and silently),
  // ensuring that the dashboard is always in a working state while respecting user preferences
  // where possible
  const parseLayoutFromConfig = (
    userConfigurations: string | null | undefined,
  ): Layouts => {
    try {
      const configs = userConfigurations
        ? JSON.parse(userConfigurations)
        : null;
      const savedLayout = configs?.dashboard?.layouts;

      if (savedLayout?.xl) {
        const newLayouts: Layouts = structuredClone(defaultLayouts);

        if (savedLayout.xl) {
          savedLayout.xl.forEach((savedItem: any) => {
            const index = newLayouts.xl.findIndex(
              (item) => item.i === savedItem.i,
            );
            if (index !== -1) {
              newLayouts.xl[index] = { ...newLayouts.xl[index], ...savedItem };
            }
          });
        }

        return newLayouts;
      }
    } catch (e) {
      console.warn("Failed to parse or apply saved layout:", e);
    }
    return defaultLayouts;
  };

  type WidgetName = keyof typeof defaultsWidgets;
  type WidgetsState = typeof defaultsWidgets;
  const parseWidgetsFromConfig = (
    userConfigurations: string | null | undefined,
  ): WidgetsState => {
    try {
      const configs = userConfigurations
        ? JSON.parse(userConfigurations)
        : null;
      const savedWidgets = configs?.dashboard?.widgets;

      if (savedWidgets) {
        const newWidgets = structuredClone(defaultsWidgets);

        (Object.entries(savedWidgets) as [WidgetName, any][]).forEach(
          ([widgetKey, widgetConfig]) => {
            if (widgetKey in newWidgets) {
              newWidgets[widgetKey] = {
                ...newWidgets[widgetKey],
                ...widgetConfig,
              };
            }
          },
        );
        return newWidgets;
      }
    } catch (e) {
      console.warn("Failed to parse or apply saved widgets:", e);
    }
    return defaultsWidgets;
  };

  const [dashboardConfigurationsState, setDashboardConfigurationState] =
    useState<{
      layouts: Layouts;
      widgets: WidgetsState;
    } | null>(null);

  const [dashboardConfigurationsOutbox, setDashboardConfigurationsOutbox] =
    useState<{
      layouts: Layouts;
      widgets: WidgetsState;
    } | null>(null);

  const [enableEditing, setEnableEditing] = useState(false);

  useEffect(() => {
    // New users will have null, undefined happens before loading completes
    if (myConfigurationsData?.me?.userConfigurations === undefined) return;

    const newLayouts = parseLayoutFromConfig(
      myConfigurationsData?.me?.userConfigurations,
    );
    const newWidgets = parseWidgetsFromConfig(
      myConfigurationsData?.me?.userConfigurations,
    );

    setDashboardConfigurationState({
      layouts: structuredClone(newLayouts),
      widgets: structuredClone(newWidgets),
    });
    setDashboardConfigurationsOutbox({
      layouts: structuredClone(newLayouts),
      widgets: structuredClone(newWidgets),
    });
  }, [myConfigurationsData?.me?.userConfigurations]);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const getDisabledWidgets = () => {
    return Object.entries(dashboardConfigurationsOutbox?.widgets ?? {})
      .filter(([, config]) => !config.enabled)
      .map(([name]) => name);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = (width: number) => {
      if (width > 0) {
        setContainerWidth(width);
      }
    };

    updateWidth(containerRef.current.clientWidth);

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      updateWidth(width);
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const ResponsiveGridLayout = useMemo(
    () => CustomWidthProvider(Responsive, containerWidth ?? undefined),
    [containerWidth],
  );
  const StyledGridLayout = useMemo(
    () => styled(ResponsiveGridLayout)(({ theme }) => gridLayoutStyles(theme)),
    [ResponsiveGridLayout],
  );

  if (containerWidth === null) {
    return (
      <Container ref={containerRef} sx={{ minWidth: "100%" }}>
        <LargeSpinner />
      </Container>
    );
  }

  const forbidden = getIsForbidden(error);
  if (forbidden) {
    return <ForbiddenPage />;
  }

  if (
    loading ||
    configurationsLoading ||
    !dashboardConfigurationsOutbox ||
    !dashboardConfigurationsState
  ) {
    return <LargeSpinner />;
  }

  return (
    <Container ref={containerRef} sx={{ minWidth: "100%" }}>
      <HeaderGrid
        crumbs={[
          {
            label:
              orgId || orgIds
                ? data?.organizations
                  ? data?.organizations
                      .map((org: Organization) => org.name)
                      .join("+")
                  : "-"
                : "Support",
          },
          { label: "Disinfections" },
        ]}
        crumbsSuffix={
          <div style={{ display: "flex", flexDirection: "row" }}>
            <HeaderIconButton
              disableRipple
              onClick={() => {
                if (enableEditing) {
                  setDashboardConfigurationsOutbox(
                    dashboardConfigurationsState,
                  );
                  enqueueSnackbar("Layout discarded", {
                    variant: "warning",
                    autoHideDuration: 3000,
                  });
                } else {
                  enqueueSnackbar("Layout editing activated", {
                    variant: "info",
                    autoHideDuration: 3000,
                  });
                }
                setEnableEditing(!enableEditing);
              }}
              className={enableEditing ? "rotating" : ""}
            >
              <IoSettingsOutline size={20} className="outline-icon" />
              <IoSettings size={20} className="filled-icon" />
            </HeaderIconButton>
            {enableEditing && (
              <>
                <HeaderIconButton
                  disableRipple
                  onClick={() => {
                    setEnableEditing(false);
                    setDashboardConfigurationState(
                      dashboardConfigurationsOutbox,
                    );
                    const snackbarId = enqueueSnackbar(
                      "Saving dashboard layout...",
                      {
                        variant: "loading",
                        persist: true,
                      },
                    );

                    updateMyConfigurations({
                      variables: {
                        payload: {
                          userConfigurations: JSON.stringify({
                            version: 1,
                            dashboard: dashboardConfigurationsOutbox,
                          }),
                        },
                      },
                      onCompleted: () => {
                        enqueueSnackbar(`Dashboard layout saved`, {
                          variant: "success",
                        });
                        closeSnackbar(snackbarId);
                      },
                      onError: () => {
                        enqueueSnackbar(
                          `Layout failed to save for next time, sorry!`,
                          {
                            variant: "error",
                          },
                        );
                        closeSnackbar(snackbarId);
                      },
                    });
                  }}
                >
                  <IoSaveOutline size={20} className="outline-icon" />
                  <IoSave size={20} className="filled-icon" />
                </HeaderIconButton>
                <HeaderIconButton
                  disableRipple
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      layouts: structuredClone(defaultLayouts),
                      widgets: structuredClone(defaultsWidgets),
                    });
                    enqueueSnackbar("Layout reset to default", {
                      variant: "info",
                    });
                  }}
                >
                  <IoRefreshOutline size={20} className="outline-icon" />
                  <IoRefresh size={20} className="filled-icon" />
                </HeaderIconButton>
                <HeaderIconButton
                  disableRipple
                  onClick={(event) => {
                    setMenuAnchorEl(event.currentTarget);
                  }}
                >
                  <IoAddOutline size={25} className="outline-icon" />
                  <IoAdd size={25} className="filled-icon" />
                </HeaderIconButton>
                <StyledTypography>
                  Layout editing active - click settings again to exit
                </StyledTypography>
              </>
            )}
          </div>
        }
      >
        <DisinfectionDashboardFilter hiddenFilter={orgIdsFilter} />
      </HeaderGrid>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        {getDisabledWidgets().map((widgetName: string) => (
          <MenuItem
            key={widgetName}
            onClick={() => {
              const newWidgets = { ...dashboardConfigurationsOutbox.widgets };
              newWidgets[widgetName as keyof typeof defaultsWidgets].enabled =
                true;

              const layoutItemId = Object.entries(widgetNameToLayoutId).find(
                ([, name]) => name === widgetName,
              )?.[0];

              if (layoutItemId) {
                const defaultXL = defaultLayouts.xl.find(
                  (item) => item.i === layoutItemId,
                );

                const newLayouts = {
                  xl: [...dashboardConfigurationsOutbox.layouts.xl],
                };

                if (defaultXL) {
                  newLayouts.xl.push(defaultXL);
                }

                setDashboardConfigurationsOutbox({
                  layouts: structuredClone(newLayouts),
                  widgets: structuredClone(newWidgets),
                });
              }
              setMenuAnchorEl(null);
            }}
          >
            {widgetName}
          </MenuItem>
        ))}
        {getDisabledWidgets().length === 0 && (
          <MenuItem disabled>All widgets are enabled</MenuItem>
        )}
      </Menu>

      <StyledGridLayout
        className="layout"
        layouts={
          enableEditing
            ? dashboardConfigurationsOutbox.layouts
            : dashboardConfigurationsState.layouts
        }
        breakpoints={{ xl: 0 }}
        cols={{ xl: 3 }}
        rowHeight={120}
        autoSize={true}
        isDraggable={enableEditing}
        isResizable={false}
        draggableHandle=".drag-handle"
        onLayoutChange={(layout: Layout, layouts: Layouts) => {
          void layout;
          setDashboardConfigurationsOutbox({
            ...dashboardConfigurationsOutbox,
            layouts,
          });
        }}
      >
        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfection Cycles Count"].enabled && (
          <div key="CyclesCounterChart">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfection Cycles Count": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <CyclesCounterChart
                  filter={{
                    ...filterParams,
                    ...orgIdsFilter,
                  }}
                  queryParams={{
                    skip: !filterParams?.timeSpan,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Unique Rooms Count"].enabled &&
          !props.isOnSupportPath && (
            <div key="UniqueRoomsChart">
              <div
                className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}
              >
                {enableEditing && (
                  <RemoveButton
                    onClick={() => {
                      setDashboardConfigurationsOutbox({
                        ...dashboardConfigurationsOutbox,
                        widgets: {
                          ...dashboardConfigurationsOutbox.widgets,
                          "Unique Rooms Count": { enabled: false },
                        },
                      });
                    }}
                  >
                    <CloseIcon />
                  </RemoveButton>
                )}
                <div className="drag-handle">
                  <UniqueRoomsChart hiddenFilter={orgIdsFilter} />
                </div>
              </div>
            </div>
          )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Unique Operators Count"].enabled && (
          <div key="UniqueOperatorsChart">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Unique Operators Count": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <UniqueOperatorsChart hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfection Count By Mode"].enabled && (
          <div key="DisinfectionModesChart">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfection Count By Mode": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <DisinfectionModesChart hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Average Duration"].enabled && (
          <div key="AverageDurationChart">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Average Duration": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <AverageDurationChart hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Positions Disinfected"].enabled && (
          <div key="PositionsReachedChart">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Positions Disinfected": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <PositionsReachedChart hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfection Count By Day"].enabled && (
          <div key="CycleOverviewChartDaily">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfection Count By Day": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <CycleOverviewChart
                  hiddenFilter={orgIdsFilter}
                  groupBy={GroupBy.Day}
                />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfection Count By Week"].enabled && (
          <div key="CycleOverviewChartWeekly">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfection Count By Week": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <CycleOverviewChart
                  hiddenFilter={orgIdsFilter}
                  groupBy={GroupBy.Week}
                />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfection Count By Month"].enabled && (
          <div key="CycleOverviewChartMonthly">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfection Count By Month": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <CycleOverviewChart
                  hiddenFilter={orgIdsFilter}
                  groupBy={GroupBy.Month}
                />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfection Reports"].enabled && (
          <div key="DisinfectionsReportsTable">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfection Reports": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <DisinfectionsReportsTable hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfection Count By Operator"].enabled && (
          <div key="DisinfectionsByOperatorTable">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfection Count By Operator": {
                          enabled: false,
                        },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <DisinfectionsByOperatorTable hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfections By Room"].enabled &&
          !props.isOnSupportPath && (
            <div key="DisinfectionsByRoomTable">
              <div
                className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}
              >
                {enableEditing && (
                  <RemoveButton
                    onClick={() => {
                      setDashboardConfigurationsOutbox({
                        ...dashboardConfigurationsOutbox,
                        widgets: {
                          ...dashboardConfigurationsOutbox.widgets,
                          "Disinfections By Room": { enabled: false },
                        },
                      });
                    }}
                  >
                    <CloseIcon />
                  </RemoveButton>
                )}
                <div className="drag-handle">
                  <DisinfectionsByRoomTable
                    hiddenFilter={orgIdsFilter}
                    pageSize={6}
                    enableDDFilter={true}
                  />
                </div>
              </div>
            </div>
          )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfections By Department"].enabled &&
          !props.isOnSupportPath && (
            <div key="DisinfectionsByDepartmentTable">
              <div
                className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}
              >
                {enableEditing && (
                  <RemoveButton
                    onClick={() => {
                      setDashboardConfigurationsOutbox({
                        ...dashboardConfigurationsOutbox,
                        widgets: {
                          ...dashboardConfigurationsOutbox.widgets,
                          "Disinfections By Department": { enabled: false },
                        },
                      });
                    }}
                  >
                    <CloseIcon />
                  </RemoveButton>
                )}
                <div className="drag-handle">
                  <DisinfectionsByDepartmentTable
                    hiddenFilter={orgIdsFilter}
                    pageSize={6}
                    enableDDFilter={true}
                  />
                </div>
              </div>
            </div>
          )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfections Completed"].enabled && (
          <div key="CompletionChart">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfections Completed": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <CompletionChart hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Unique Departments Count"].enabled &&
          !props.isOnSupportPath && (
            <div key="UniqueDepartmentsChart">
              <div
                className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}
              >
                {enableEditing && (
                  <RemoveButton
                    onClick={() => {
                      setDashboardConfigurationsOutbox({
                        ...dashboardConfigurationsOutbox,
                        widgets: {
                          ...dashboardConfigurationsOutbox.widgets,
                          "Unique Departments Count": { enabled: false },
                        },
                      });
                    }}
                  >
                    <CloseIcon />
                  </RemoveButton>
                )}
                <div className="drag-handle">
                  <UniqueDepartmentsChart hiddenFilter={orgIdsFilter} />
                </div>
              </div>
            </div>
          )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfections Interrupted"].enabled && (
          <div key="DisinfectionsInterruptedChart">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Disinfections Interrupted": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <DisinfectionsInterruptedChart hiddenFilter={orgIdsFilter} />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Interruptions Count By Day"].enabled && (
          <div key="InterruptionsCountByDay">
            <div className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}>
              {enableEditing && (
                <RemoveButton
                  onClick={() => {
                    setDashboardConfigurationsOutbox({
                      ...dashboardConfigurationsOutbox,
                      widgets: {
                        ...dashboardConfigurationsOutbox.widgets,
                        "Interruptions Count By Day": { enabled: false },
                      },
                    });
                  }}
                >
                  <CloseIcon />
                </RemoveButton>
              )}
              <div className="drag-handle">
                <InterruptionsOverviewChart
                  hiddenFilter={orgIdsFilter}
                  groupBy={GroupBy.Day}
                />
              </div>
            </div>
          </div>
        )}

        {(enableEditing
          ? dashboardConfigurationsOutbox
          : dashboardConfigurationsState
        ).widgets["Disinfections Per Room Across Time"].enabled &&
          !props.isOnSupportPath && (
            <div key="DisinfectionsPerRoomAcrossTime">
              <div
                className={`wiggle-wrapper ${enableEditing ? "active" : ""}`}
              >
                {enableEditing && (
                  <RemoveButton
                    onClick={() => {
                      setDashboardConfigurationsOutbox({
                        ...dashboardConfigurationsOutbox,
                        widgets: {
                          ...dashboardConfigurationsOutbox.widgets,
                          "Disinfections Per Room Across Time": {
                            enabled: false,
                          },
                        },
                      });
                    }}
                  >
                    <CloseIcon />
                  </RemoveButton>
                )}
                <div className="drag-handle">
                  <DisinfectionsPerRoomAcrossTimeGraph
                    hiddenFilter={orgIdsFilter}
                  />
                </div>
              </div>
            </div>
          )}
      </StyledGridLayout>
    </Container>
  );
};
